import { Fragment, type FunctionComponent, useMemo } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Box, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';

import StarCircle from 'components/Icons/StarCircle';
import ForecasterProfile from 'components/ForecasterProfile';
import SubregionForecastLink from 'components/SubregionForecastLink';
import type { ForecastContent } from 'types/subregion';

import styles from './SubregionForecastContentHighlights.module.scss';

const getDayOfWeek = (dateString: string): string =>
  format(new Date(`${dateString}T00:00:00`), 'EEE');

interface Props {
  className?: string;
  elevation?: number;
  forecastContent?: ForecastContent;
  isLoading?: boolean;
  isTodayIncluded?: boolean;
  pageName?: string;
}

const SubregionForecastContentHighlights: FunctionComponent<Props> = ({
  className,
  elevation,
  forecastContent,
  isLoading,
  isTodayIncluded = true,
  pageName,
}) => {
  const forecastHighlights = useMemo(() => {
    const highlights =
      forecastContent?.highlights.slice(0, 7).filter((day) => day.forecast !== null) ?? [];
    if (forecastContent?.tomorrow.forecast.dayToWatch) {
      highlights.unshift({
        date: forecastContent.tomorrow.date,
        forecast: forecastContent.tomorrow.forecast,
      });
    }
    if (isTodayIncluded && forecastContent?.today.forecast.dayToWatch) {
      highlights.unshift({
        date: forecastContent.today.date,
        forecast: forecastContent.today.forecast,
      });
    }
    return highlights;
  }, [forecastContent, isTodayIncluded]);

  const highlightsContent = useMemo(
    () =>
      forecastHighlights.map((day) => {
        return (
          <Fragment key={`day-${day.date}`}>
            <Grid item mobile={2} tablet={1}>
              <Typography component="h3" variant="callout1">
                {getDayOfWeek(day.date)}:
              </Typography>
            </Grid>
            <Grid item mobile={10} tablet={11}>
              <Typography component="h4" variant="body1" className={styles.cardText}>
                {day.forecast?.headline}
              </Typography>
            </Grid>
          </Fragment>
        );
      }),
    [forecastHighlights],
  );

  if (isLoading)
    return (
      <Skeleton
        animation="wave"
        className={styles.loadingHighlights}
        data-testid="subregion-forecast-content-highlights-loading"
        variant="rectangular"
      />
    );

  if (!forecastContent || !forecastHighlights.length) return null;

  return (
    <Card
      className={classNames(styles.forecasterContentHighlights, className)}
      component="section"
      data-testid="subregion-forecast-content-highlights"
      elevation={elevation}
    >
      <Box padding={1}>
        <Stack component="header" direction="row">
          <Box sx={{ width: '100%' }}>
            <ForecasterProfile
              conditions=""
              iconUrl={forecastContent.forecaster.avatarUrl}
              isShowConditionsBorder={false}
              isShowName
              lastUpdate={{ timestamp: forecastContent.lastPublished }}
              name={forecastContent.forecaster.name}
              title={
                <>
                  <StarCircle className={styles.star} /> Days to Watch
                </>
              }
            />
          </Box>
          <Box>
            <SubregionForecastLink
              contentBlock="highlights"
              forecasterName={forecastContent.forecaster.name}
              pageName={pageName}
              start="forecast-highlights"
              subregionId={forecastContent.subregion.id}
              subregionName={forecastContent.subregion.name}
            />
          </Box>
        </Stack>
        <Grid container spacing={2} mt={2}>
          {highlightsContent}
        </Grid>
      </Box>
    </Card>
  );
};

export default SubregionForecastContentHighlights;
